import React from 'react';
import { View, Pressable, Text, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { WatchHistory } from '../types/domain';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface ContinueWatchingRowProps {
  items: WatchHistory[];
  isLoading?: boolean;
}

export function ContinueWatchingRow({ items, isLoading = false }: ContinueWatchingRowProps) {
  const navigate = useNavigate();

  if (items.length === 0 && !isLoading) {
    return null;
  }

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Continuar Assistindo</Text>

      <View style={styles.grid}>
        {items.map((item) => {
          const progress = (item.watched_seconds / item.duration_seconds) * 100;
          const minutesRemaining = Math.round((item.duration_seconds - item.watched_seconds) / 60);

          return (
            <Pressable
              key={item.id}
              style={styles.card}
              onPress={() => navigate(`/watch/${item.series_id}/${item.episode_number || 1}`)}
            >
              {/* Thumbnail com imagem */}
              <View style={styles.thumbnail}>
                {item.thumbnail_url ? (
                  <>
                    <img
                      src={item.thumbnail_url}
                      style={imgStyle}
                      alt="Episode thumbnail"
                    />
                  </>
                ) : (
                  <View
                    style={[StyleSheet.absoluteFill, { backgroundColor: NETFLIX_COLORS.surface }]}
                  />
                )}
                <View style={styles.playOverlay}>
                  <Play size={40} color={NETFLIX_COLORS.white} />
                </View>
              </View>

              {/* Barra de progresso */}
              <View style={styles.progressContainer}>
                <View style={styles.progressBar}>
                  <View
                    style={[
                      styles.progressFill,
                      { width: `${Math.min(progress, 100)}%` },
                    ]}
                  />
                </View>
              </View>

              {/* Informações */}
              <View style={styles.info}>
                <Text style={styles.seriesTitle} numberOfLines={1}>
                  {item.title || 'Série'}
                </Text>
                <Text style={styles.episodeInfo} numberOfLines={1}>
                  Ep. {item.episode_number || 1} • {minutesRemaining}m restante
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: NETFLIX_SPACING.lg,
    marginBottom: NETFLIX_SPACING.xl,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: NETFLIX_COLORS.white,
    marginBottom: NETFLIX_SPACING.lg,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: NETFLIX_SPACING.md,
  },
  card: {
    width: '48%',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: NETFLIX_COLORS.surface,
  },
  thumbnail: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: NETFLIX_COLORS.surface,
    position: 'relative',
  },
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressContainer: {
    paddingHorizontal: NETFLIX_SPACING.sm,
    paddingTop: NETFLIX_SPACING.xs,
  },
  progressBar: {
    height: 3,
    backgroundColor: NETFLIX_COLORS.surfaceHover,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: NETFLIX_COLORS.primary,
  },
  info: {
    padding: NETFLIX_SPACING.sm,
    backgroundColor: NETFLIX_COLORS.surfaceHover,
  },
  seriesTitle: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  episodeInfo: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 12,
  },
});

const imgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
} as React.CSSProperties;
